import { formatDateKey, formatDateKeyRange } from '@apps/web/lib/date_utils';
import { type LlmProviderBillingTotal } from '@libraries/typescript/api_client';

const PROVIDER_LABELS: Readonly<Record<string, string>> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google Cloud',
    aws: 'AWS Bedrock',
};

export function providerLabel(provider: string): string {
    return PROVIDER_LABELS[provider] ?? provider;
}

// Amounts arrive as decimal strings. Rendering to cents through Number is
// exact for any bill a dashboard will show.
export function formatProviderMoney(amount: string, currency: string): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(Number(amount));
}

// Providers bill in their own currency, so totals stay per currency rather
// than converted at a rate the dashboard would have to invent.
export function formatProviderTotals(
    totals: ReadonlyArray<LlmProviderBillingTotal>,
): string {
    return totals
        .map((total) => formatProviderMoney(total.amount, total.currency))
        .join(' · ');
}

function previousDateKey(dateKey: string): string {
    const date = new Date(`${dateKey}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().slice(0, 10);
}

// Describes which part of the requested range an import has reached, or null
// when nothing has been imported. Dates are UTC billing dates, not
// organization-local times.
export function coverageNote(
    coverage: {
        readonly covered_from: string | null;
        readonly covered_through: string | null;
    },
    range: { readonly range_start: string; readonly range_end: string },
): string | null {
    if (coverage.covered_from === null || coverage.covered_through === null) {
        return null;
    }
    const lastRangeDay = previousDateKey(range.range_end);
    const through =
        coverage.covered_through < lastRangeDay
            ? coverage.covered_through
            : lastRangeDay;
    if (coverage.covered_from > range.range_start) {
        return `Imported ${formatDateKeyRange(coverage.covered_from, through, 'UTC')}`;
    }
    return `Through ${formatDateKey(through, 'UTC')}`;
}

// The span every provider has imported: the latest start and earliest end,
// or null when any provider has nothing yet.
export function combinedCoverage(
    providers: ReadonlyArray<{
        readonly covered_from: string | null;
        readonly covered_through: string | null;
    }>,
): {
    readonly covered_from: string | null;
    readonly covered_through: string | null;
} {
    let coveredFrom: string | null = null;
    let coveredThrough: string | null = null;
    for (const provider of providers) {
        if (
            provider.covered_from === null ||
            provider.covered_through === null
        ) {
            return { covered_from: null, covered_through: null };
        }
        if (coveredFrom === null || provider.covered_from > coveredFrom) {
            coveredFrom = provider.covered_from;
        }
        if (
            coveredThrough === null ||
            provider.covered_through < coveredThrough
        ) {
            coveredThrough = provider.covered_through;
        }
    }
    return { covered_from: coveredFrom, covered_through: coveredThrough };
}
